/**
 * akten-thumbnails — Logik für die Auswahl des besten Fahrzeug-Thumbnails
 *
 * Priorisierung:
 * 1. "Übersichtsaufnahme vorne links" (WinValue-Benennung)
 * 2. Beliebige Übersichtsaufnahme
 * 3. Beliebige Frontaufnahme (vorne)
 * 4. Neuestes Bild als Fallback
 */

import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';

export interface ThumbnailDoc {
  id: string;
  dateiname: string;
  titel: string;
  mime_type: string;
  dateipfad: string;
  erstellt_am: string;
}

/**
 * Wählt das beste Thumbnail-Foto aus einer Liste von Dokumenten.
 * Bevorzugt Übersichtsaufnahme vorne links, dann Übersicht, dann vorne, dann neuestes.
 */
export function pickBestThumbnail(docs: ThumbnailDoc[] | null | undefined): ThumbnailDoc | null {
  if (!docs || docs.length === 0) return null;

  // Nur Bilder berücksichtigen
  const images = docs.filter(d => d.mime_type && d.mime_type.startsWith('image/'));
  if (images.length === 0) return null;

  // Normalisiere Text für Matching: Umlaute auflösen, lowercase
  const normalize = (s: string) =>
    (s || '').toLowerCase()
      .replace(/ü/g, 'ue')
      .replace(/ö/g, 'oe')
      .replace(/ä/g, 'ae')
      .replace(/ß/g, 'ss')
      .replace(/[_\-./\\]/g, ' ');

  const matchText = (doc: ThumbnailDoc) =>
    normalize(doc.titel) + ' ' + normalize(doc.dateiname);

  // Priorität 1: "Übersichtsaufnahme vorne links" (exakt oder nah)
  const vorneLinksMatch = images.find(d => {
    const text = matchText(d);
    return (text.includes('uebersicht') || text.includes('ue ')) &&
           text.includes('vorne') &&
           text.includes('links');
  });
  if (vorneLinksMatch) return vorneLinksMatch;

  // Priorität 2: Beliebige Übersichtsaufnahme
  const uebersichtMatch = images.find(d => {
    const text = matchText(d);
    return text.includes('uebersicht');
  });
  if (uebersichtMatch) return uebersichtMatch;

  // Priorität 3: Vorne/Front-Aufnahme
  const vorneMatch = images.find(d => {
    const text = matchText(d);
    return text.includes('vorne') || text.includes('front');
  });
  if (vorneMatch) return vorneMatch;

  // Fallback: neuestes Bild
  const sorted = [...images].sort((a, b) =>
    (b.erstellt_am || '').localeCompare(a.erstellt_am || '')
  );
  return sorted[0];
}

/**
 * React Hook: Batch-lädt Thumbnails für eine Liste von Akten-IDs.
 * Gibt eine Map<akteId, signedUrl> zurück.
 *
 * - Fetcht alle Lichtbilder/Fotos für die gegebenen IDs in einer Query
 * - Wählt pro Akte das beste Thumbnail mit pickBestThumbnail
 * - Erzeugt Signed URLs für die ausgewählten Bilder
 * - Cached bereits geladene URLs, damit Scroll/Re-Render nicht neu lädt
 */
export function useAktenThumbnails(akteIds: string[]): Record<string, string> {
  const [urlMap, setUrlMap] = useState<Record<string, string>>({});
  const cacheRef = useRef<Record<string, string>>({});
  const pendingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (akteIds.length === 0) return;

    // Nur IDs laden, die wir noch nicht haben
    const newIds = akteIds.filter(id => !cacheRef.current[id] && !pendingRef.current.has(id));
    if (newIds.length === 0) return;

    newIds.forEach(id => pendingRef.current.add(id));

    let cancelled = false;

    (async () => {
      // Supabase IN-Filter: max ~300 IDs pro Batch
      const BATCH_SIZE = 200;
      const allDocs: (ThumbnailDoc & { akte_id: string })[] = [];

      for (let i = 0; i < newIds.length; i += BATCH_SIZE) {
        const batch = newIds.slice(i, i + BATCH_SIZE);
        const { data } = await supabase
          .from('dokumente')
          .select('id,akte_id,dateiname,dateipfad,mime_type,titel,erstellt_am')
          .in('akte_id', batch)
          .in('kategorie', ['lichtbilder', 'fotos'])
          .is('geloescht_am', null);
        if (data) allDocs.push(...(data as any[]));
      }

      if (cancelled) return;

      // Gruppiere nach akte_id und wähle das beste Thumbnail
      const grouped = new Map<string, ThumbnailDoc[]>();
      for (const doc of allDocs) {
        const list = grouped.get(doc.akte_id) || [];
        list.push(doc);
        grouped.set(doc.akte_id, list);
      }

      const selectedPaths: { akteId: string; path: string }[] = [];
      for (const [akteId, docs] of grouped) {
        const best = pickBestThumbnail(docs);
        if (best) selectedPaths.push({ akteId, path: best.dateipfad });
      }

      if (selectedPaths.length === 0) {
        newIds.forEach(id => pendingRef.current.delete(id));
        return;
      }

      // Signed URLs in einem Batch erzeugen
      const { data: signedData } = await supabase.storage
        .from('dokumente')
        .createSignedUrls(
          selectedPaths.map(s => s.path),
          3600
        );

      if (cancelled) return;

      const newUrls: Record<string, string> = {};
      if (signedData) {
        for (let i = 0; i < selectedPaths.length; i++) {
          const url = signedData[i]?.signedUrl;
          if (url) {
            newUrls[selectedPaths[i].akteId] = url;
            cacheRef.current[selectedPaths[i].akteId] = url;
          }
        }
      }

      newIds.forEach(id => pendingRef.current.delete(id));

      if (Object.keys(newUrls).length > 0) {
        setUrlMap(prev => ({ ...prev, ...newUrls }));
      }
    })();

    return () => { cancelled = true; };
  }, [akteIds.join(',')]);

  return urlMap;
}
